import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Bundle} from '@sportngin/se-commerce-sdk/models/clare';
import {environment} from '../../environments/environment';
import {
  ApiResponse,
  Catalog,
  CatalogAssociationQueryParams,
  CatalogCreateRequest,
  CatalogsQueryParams,
  CatalogSummary,
  DEFAULT_CATALOGS_QUERY_PARAMS,
  PagedApiResponse,
} from '../models/clare';
import {objectToHttpParams, pluckResult, ProductManagementApiService} from './product-management-api.service';

@Injectable({providedIn: 'root'})
export class CatalogApiService extends ProductManagementApiService {
  controllerPath = '/catalogs';

  constructor(protected override http: HttpClient) {
    super(http);
  }

  getCatalogByKey(key: string): Observable<Catalog> {
    const url = this.getUrl(`finance/catalogs/${key}`);
    return this.GET<ApiResponse<Catalog>>(url, this.requestOptions()).pipe(pluckResult());
  }

  getCatalogsByOrganizationId(
    organizationId: string,
    queryParams: Partial<CatalogsQueryParams> = {}
  ): Observable<PagedApiResponse<CatalogSummary>> {
    const url = this.getUrl(`finance/organizations/${organizationId}/catalogs`);
    return this.GET<PagedApiResponse<CatalogSummary>>(url, {
      ...this.requestOptions(),
      params: objectToHttpParams({...DEFAULT_CATALOGS_QUERY_PARAMS, ...queryParams}),
    });
  }

  getAllCatalogsByOrganizationId(
    organizationId: string,
    queryParams?: CatalogAssociationQueryParams
  ): Observable<CatalogSummary[]> {
    const url = this.getUrl(`finance/organizations/${organizationId}/catalogs`);
    return this.GET<ApiResponse<CatalogSummary[]>>(url, {
      ...this.requestOptions(),
      params: objectToHttpParams({unpaginated: true, ...queryParams}),
    }).pipe(pluckResult());
  }

  getBundlesByCatalogId(catalogId: number | string): Observable<Bundle[]> {
    const url = this.getUrl(`finance/bundles?catalog_id=${catalogId}`);
    return this.GET<ApiResponse<Bundle[]>>(url, this.requestOptions()).pipe(
      pluckResult(),
      map(bundles => (Array.isArray(bundles) ? bundles : []))
    );
  }

  create(catalog: CatalogCreateRequest): Observable<Catalog> {
    const url = this.getUrl(`finance/organizations/${catalog.organization_id}/catalogs`);
    return this.POST<ApiResponse<Catalog>>(url, catalog, this.requestOptions()).pipe(pluckResult());
  }

  getById(organizationId: string, catalogId: string): Observable<Catalog> {
    const url = this.getUrl(`finance/organizations/${organizationId}/catalogs/${catalogId}`);
    return this.GET<ApiResponse<Catalog>>(url, this.requestOptions()).pipe(pluckResult());
  }

  update(catalog: Catalog, organizationId: string): Observable<Catalog> {
    const url = this.getUrl(`finance/organizations/${organizationId}/catalogs/${catalog.id}`);
    return this.PATCH<ApiResponse<Catalog>>(url, catalog, this.requestOptions()).pipe(pluckResult());
  }

  delete(organizationId: string, catalogId: number): Observable<void> {
    const url = this.getUrl(`finance/organizations/${organizationId}/catalogs/${catalogId}`);
    return this.DELETE<void>(url, this.requestOptions());
  }

  protected override getUrl(path = ''): string {
    return `${environment.apiBaseUrl}/${path}`;
  }
}
